import { Link, useLocation } from "react-router-dom";

type FooterVariant = "main" | "corporate";

interface FooterProps {
  variant?: FooterVariant;
}

export default function Footer({ variant = "main" }: FooterProps) {
  const location = useLocation();

  // Check if we're on login or register pages
  const isAuthPage =
    location.pathname === "/login" || location.pathname === "/register";

  if (variant === "corporate") {
    return (
      <footer className="bg-dominos-footer-dark text-white md:pt-20 pb-10">
        <div className="max-w-6xl lg:h-[300px] mx-auto pt-10 flex flex-col md:flex-row md:justify-between gap-8">
          {/* Domino's Logo */}
          <div className="flex flex-col justify-start px-4 md:px-0   md:justify-between md:items-center md:items-start gap-4 md:gap-2">
            <Link to="/">
              <img
                src="https://cdn.builder.io/api/v1/image/assets/TEMP/a916f4e4c63a0e0c9d73ce1081254150f5de44c9?width=663"
                alt="Domino's Footer Logo"
                className="w-[170px] h-[50px] object-contain"
              />
            </Link>
            <div className="md:text-center text-xs text-white mt-4 pb-3">
              © {new Date().getFullYear()} Domino's. All Rights Reserved
            </div>
          </div>
          {/* Links */}
          <div className="flex col-span-6 px-4  mt-6 flex flex-wrap  md:justify-start gap-8">
            <ul className="space-y-5 gap-3 flex flex-col">
              <li className="flex items-center space-x-3">
                <Link
                  to="/terms-of-use"
                  className="text-white text-[16px] font-bold leading-[16px] hover:underline"
                >
                  Terms of Use
                </Link>
              </li>
              <li className="flex items-center space-x-2">
                <Link
                  to="/privacy-policy"
                  className="text-white text-[16px]  font-bold leading-[16px] hover:underline"
                >
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>
          <div className="flex col-span-6   mt-6 flex flex-wrap justify-start px-4 md:justify-start gap-8">
            <ul className="space-y-5 gap-3 flex flex-col">
              <li className="flex items-center space-x-3">
                <Link
                  to="/investors/sharePrice#slavery"
                  className="text-white text-[16px] font-bold leading-[16px] hover:underline"
                >
                  Modern Slavery Statement
                </Link>
              </li>
              <li className="flex items-center space-x-3">
                <Link
                  to="/corporate/about/our-food/food-faqs"
                  className="text-white text-[16px] font-bold leading-[16px] hover:underline"
                >
                  FAQs
                </Link>
              </li>
              <li className="flex items-center space-x-2">
                <Link
                  to="/corporate/contact"
                  className="text-white text-[16px]  font-bold leading-[16px] hover:underline"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>
          {/* Simple social icons row for corporate footer */}
          <div className="flex justify-start px-4 lg:justify-end space-x-3 mt-6 mb-8">
            <a
              href="https://www.facebook.com/DominosPizza"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full border border-white text-xs font-semibold hover:bg-white hover:text-dominos-blue"
              aria-label="Domino's on Facebook"
            >
              FB
            </a>
            <a
              href="https://twitter.com/Dominos_UK"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full border border-white text-xs font-semibold hover:bg-white hover:text-dominos-blue"
              aria-label="Domino's on X (Twitter)"
            >
              X
            </a>
            <a
              href="https://www.instagram.com/dominos_uk/?hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full border border-white text-xs font-semibold hover:bg-white hover:text-dominos-blue"
              aria-label="Domino's on Instagram"
            >
              IG
            </a>
          </div>
        </div>
      </footer>
    );
  }

  // On auth pages, render minimal footer with only legal links
  if (isAuthPage) {
    return (
      <footer>
        {/* Legal Footer Only - on auth pages */}
        <div className="bg-dominos-light-gray py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-wrap justify-center items-center gap-2 text-xs font-bold text-dominos-blue mb-4 leading-[31px]">
              <Link to="/terms-of-use" className="hover:underline">
                Terms of Use
              </Link>
              <span>|</span>
              <Link to="/privacy-policy" className="hover:underline">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-dominos-blue text-white">
      {/* Main Footer Content */}
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-16">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-8 sm:gap-12">
          {/* Left Section: Follow Us - Social icons section */}
          <div className="flex flex-col justify-start sm:col-span-1">
            {/* Heading hidden on mobile to match Domino's UK */}
            <h3 className="hidden sm:block text-white text-4xl uppercase mb-8 leading-[39px]">
              Follow Us
            </h3>

            {/* Social Media Icons - centered on mobile, left-aligned on desktop */}
            <div className="flex justify-center sm:justify-start space-x-3 mb-8 pt-4 sm:pt-0">
              {/* Social Icons */}
              <a
                href="https://www.facebook.com/DominosPizza"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Domino's on Facebook"
                className="w-9 h-9 flex items-center justify-center hover:opacity-80"
              >
                <svg
                  width="36"
                  height="37"
                  viewBox="0 0 36 37"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M31.22 3.86328H4.648C3.725 3.86328 3 4.58828 3 5.51128V32.1163C3 33.0393 3.725 33.7643 4.648 33.7643H18.956V22.1933H15.066V17.6763H18.956V14.3463C18.956 10.4893 21.33 8.37928 24.758 8.37928C26.407 8.37928 27.824 8.51128 28.253 8.54428V12.5663H25.879C24 12.5663 23.637 13.4563 23.637 14.7763V17.6433H28.121L27.494 22.1933H23.604V33.7643H31.22C32.143 33.7643 32.868 33.0393 32.868 32.1163V5.54328C32.901 4.62128 32.143 3.86328 31.22 3.86328Z"
                    fill="white"
                  />
                </svg>
              </a>
              <a
                href="https://twitter.com/Dominos_UK"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Domino's on X (Twitter)"
                className="w-9 h-9 flex items-center justify-center hover:opacity-80"
              >
                <svg
                  width="36"
                  height="37"
                  viewBox="0 0 36 37"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20.669 17.168L31.279 4.875H28.764L19.552 15.549L12.194 4.875H3.70703L14.834 21.016L3.70703 33.908H6.22103L15.95 22.635L23.72 33.908H32.207L20.667 17.168H20.669ZM17.225 21.158L16.097 19.551L7.12703 6.761H10.989L18.229 17.083L19.356 18.69L28.766 32.107H24.903L17.225 21.158Z"
                    fill="white"
                  />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/dominos_uk/?hl=en"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Domino's on Instagram"
                className="w-9 h-9 flex items-center justify-center hover:opacity-80"
              >
                <svg
                  width="36"
                  height="37"
                  viewBox="0 0 36 37"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M19.16 3.86328C22.118 3.87028 22.691 3.90328 24.098 3.96128C25.68 4.02728 26.768 4.29128 27.724 4.65328C28.68 5.01628 29.537 5.54328 30.361 6.36728C31.186 7.19128 31.681 8.01628 32.076 9.00528C32.438 9.96128 32.702 11.0493 32.768 12.6313C32.834 14.2133 32.868 14.7413 32.868 18.7963V19.2323C32.865 22.8923 32.831 23.4373 32.768 24.9613C32.702 26.5433 32.438 27.6313 32.076 28.5873C31.7037 29.581 31.1183 30.481 30.361 31.2243C29.537 32.0493 28.713 32.5443 27.724 32.9393C26.768 33.3013 25.68 33.5653 24.098 33.6313C22.691 33.6903 22.118 33.7223 19.16 33.7293H16.706C13.748 33.7223 13.175 33.6893 11.768 33.6313C10.186 33.5653 9.098 33.3013 8.142 32.9393C7.1483 32.5671 6.24821 31.9817 5.505 31.2243C4.68 30.4003 4.185 29.5763 3.79 28.5873C3.428 27.6313 3.164 26.5433 3.098 24.9613C3.039 23.5543 3.007 22.9813 3 20.0233V17.5693C3.004 15.5973 3.02 14.6853 3.048 13.8793L3.058 13.5803L3.098 12.6313C3.164 11.0493 3.428 9.96128 3.79 9.00528C4.16201 8.01118 4.74742 7.11071 5.505 6.36728C6.329 5.54328 7.153 5.04928 8.142 4.65328C9.098 4.29028 10.186 4.02728 11.768 3.96128C13.175 3.90228 13.748 3.87028 16.706 3.86328H19.16ZM20.088 6.53328H17.07C13.837 6.53728 13.332 6.57028 11.867 6.63128C10.417 6.69728 9.625 6.96128 9.097 7.15828C8.406 7.42228 7.911 7.75228 7.384 8.27928C6.856 8.80728 6.527 9.30128 6.263 9.99428C6.033 10.5213 5.801 11.3123 5.735 12.7633C5.658 13.9933 5.641 14.5463 5.637 16.6083V19.6263C5.641 22.8593 5.674 23.3643 5.735 24.8293C5.801 26.2793 6.065 27.0713 6.263 27.5983C6.527 28.2903 6.856 28.7853 7.383 29.3123C7.911 29.8403 8.406 30.1693 9.098 30.4333C9.625 30.6643 10.416 30.8953 11.868 30.9613C13.098 31.0383 13.651 31.0553 15.712 31.0593H19.105C22.005 31.0523 22.526 31.0193 23.933 30.9613C25.383 30.8953 26.175 30.6313 26.703 30.4333C27.394 30.1693 27.889 29.8403 28.417 29.3133C28.944 28.7853 29.274 28.2903 29.537 27.5983C29.768 27.0713 29.999 26.2793 30.065 24.8283C30.131 23.2463 30.164 22.7853 30.164 18.7963V18.3663C30.161 14.7713 30.128 14.2863 30.065 12.7633C29.999 11.3133 29.735 10.5213 29.537 9.99328C29.274 9.30128 28.944 8.80728 28.417 8.27928C27.889 7.75228 27.394 7.42228 26.702 7.15928C26.175 6.92828 25.384 6.69728 23.932 6.63128C22.702 6.55428 22.15 6.53728 20.088 6.53328ZM17.9 11.1153C18.9091 11.1136 19.9086 11.311 20.8412 11.6964C21.7738 12.0817 22.6212 12.6473 23.3348 13.3608C24.0483 14.0743 24.6141 14.9216 24.9995 15.8542C25.385 16.7867 25.5826 17.7862 25.581 18.7953C25.5828 19.8044 25.3855 20.804 25.0002 21.7368C24.6149 22.6695 24.0493 23.517 23.3358 24.2307C22.6223 24.9444 21.775 25.5102 20.8423 25.8957C19.9097 26.2812 18.9102 26.4789 17.901 26.4773C16.8918 26.4791 15.8921 26.2817 14.9593 25.8964C14.0265 25.511 13.1789 24.9453 12.4652 24.2317C11.7516 23.5181 11.1858 22.6707 10.8003 21.7379C10.4148 20.8052 10.2173 19.8055 10.219 18.7963C10.2173 17.7871 10.4148 16.7875 10.8002 15.8549C11.1856 14.9222 11.7513 14.0748 12.4649 13.3612C13.1785 12.6476 14.0259 12.0819 14.9586 11.6965C15.8913 11.3111 16.8908 11.1136 17.9 11.1153ZM17.9 13.8183C15.164 13.8183 12.922 16.0603 12.922 18.7963C12.922 21.5323 15.164 23.7743 17.9 23.7743C18.5539 23.7748 19.2014 23.6464 19.8056 23.3964C20.4098 23.1464 20.9588 22.7798 21.4212 22.3174C21.8835 21.8551 22.2502 21.3061 22.5001 20.7019C22.7501 20.0977 22.8785 19.4501 22.878 18.7963C22.8785 18.1424 22.7501 17.4949 22.5001 16.8907C22.2502 16.2865 21.8835 15.7375 21.4212 15.2751C20.9588 14.8128 20.4098 14.4461 19.8056 14.1961C19.2014 13.9462 18.5539 13.8178 17.9 13.8183ZM25.878 9.00528C26.868 9.00528 27.691 9.82928 27.658 10.7853C27.658 11.7743 26.868 12.5653 25.878 12.5653C24.889 12.5653 24.098 11.7743 24.098 10.7853C24.098 9.79528 24.889 9.00528 25.878 9.00528Z"
                    fill="white"
                  />
                </svg>
              </a>
              <a
                href="https://www.youtube.com/channel/UC2rjTzAKJmjvdbr4ssII4fg"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Domino's on YouTube"
                className="w-9 h-9 flex items-center justify-center hover:opacity-80"
              >
                <svg
                  width="36"
                  height="37"
                  viewBox="0 0 36 37"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14.703 23.7673V14.2073L22.517 18.9873L14.703 23.7673ZM32.308 11.1733C31.978 9.78928 30.956 8.66828 29.67 8.30528C27.33 7.61328 17.967 7.61328 17.967 7.61328C17.967 7.61328 8.604 7.61328 6.297 8.30528C5.011 8.66828 3.989 9.78928 3.659 11.1733C3 13.6793 3 18.9533 3 18.9533C3 18.9533 3 24.2283 3.626 26.7673C3.956 28.1513 4.978 29.2723 6.264 29.6353C8.604 30.3273 17.934 30.3273 17.934 30.3273C17.934 30.3273 27.297 30.3273 29.604 29.6353C30.89 29.2723 31.912 28.1523 32.242 26.7673C32.868 24.2283 32.868 18.9543 32.868 18.9543C32.868 18.9543 32.934 13.6783 32.308 11.1733Z"
                    fill="white"
                  />
                </svg>
              </a>
              <a
                href="https://www.tiktok.com/@dominos_uki"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Domino's on TikTok"
                className="w-9 h-9 flex items-center justify-center hover:opacity-80"
              >
                <svg
                  width="36"
                  height="37"
                  viewBox="0 0 36 37"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M29.429 3.86328H6.57C5.62341 3.8646 4.71598 4.24129 4.04674 4.91073C3.37749 5.58016 3.00106 6.48769 3 7.43428V30.2933C3.00132 31.2399 3.37801 32.1473 4.04744 32.8165C4.71688 33.4858 5.62441 33.8622 6.571 33.8633H29.43C30.3766 33.862 31.284 33.4853 31.9533 32.8158C32.6225 32.1464 32.9989 31.2389 33 30.2923V7.43328C32.9987 6.48669 32.622 5.57927 31.9526 4.91002C31.2831 4.24077 30.3756 3.86434 29.429 3.86328ZM26.576 16.9513C25.6082 17.041 24.6342 16.8655 23.7586 16.4435C22.883 16.0216 22.1389 15.3692 21.606 14.5563V22.7963C21.606 24.0007 21.2489 25.178 20.5798 26.1795C19.9107 27.1809 18.9597 27.9615 17.847 28.4225C16.7343 28.8835 15.5099 29.0042 14.3286 28.7694C13.1473 28.5346 12.0622 27.9548 11.2104 27.1033C10.3586 26.2518 9.77847 25.1668 9.54326 23.9856C9.30805 22.8044 9.42837 21.58 9.889 20.4671C10.3496 19.3543 11.1299 18.403 12.1311 17.7336C13.1324 17.0642 14.3096 16.7067 15.514 16.7063C15.641 16.7063 15.766 16.7173 15.891 16.7263V19.7263C15.766 19.7123 15.643 19.6893 15.514 19.6893C14.6992 19.7039 13.9228 20.0378 13.3517 20.6192C12.7807 21.2006 12.4608 21.9829 12.4608 22.7978C12.4608 23.6127 12.7807 24.395 13.3517 24.9764C13.9228 25.5577 14.6992 25.8917 15.514 25.9063C17.231 25.9063 18.748 24.5533 18.748 22.8363L18.778 8.84028H21.649C21.7801 10.0881 22.3458 11.25 23.247 12.1229C24.1482 12.9958 25.3277 13.5241 26.579 13.6153V16.9513"
                    fill="white"
                  />
                </svg>
              </a>
            </div>
          </div>

          {/* Right Section: Domino's Logo - centered on mobile, right-aligned on desktop */}
          {!isAuthPage && (
            <div className="flex items-center justify-center sm:justify-end">
              <Link to="/">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets/TEMP/a916f4e4c63a0e0c9d73ce1081254150f5de44c9?width=663"
                  alt="Domino's Footer Logo"
                  className="w-[256px] h-[56px] object-contain cursor-pointer"
                />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Legal Footer */}
      <div className="bg-dominos-light-gray py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-2 text-xs font-bold text-dominos-blue mb-4 leading-[31px]">
            <Link to="/" className="hover:underline">
              Pizza Near Me
            </Link>
            <span>|</span>
            <Link to="/terms-of-use" className="hover:underline">
              Terms of Use
            </Link>
            <span>|</span>
            <Link
              to="/terms-and-conditions-of-sale"
              className="hover:underline"
            >
              Terms and Conditions
            </Link>
            <span>|</span>
            <Link to="/privacy-policy" className="hover:underline">
              Privacy Policy
            </Link>
            <span>|</span>
            <Link to="/copyright-and-legal" className="hover:underline">
              Copyright and Legal
            </Link>
            <span>|</span>
            <Link to="/boring-legal-stuff" className="hover:underline">
              Boring Legal Stuff
            </Link>
          </div>
          <div className="text-center text-xs font-bold text-black leading-[20px]">
            <span>© 2026 Domino's Pizza UK and Ireland Limited</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
